import noop from 'lodash/noop';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	useActiveRightSidebarState,
	useRightSidebarController,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import {
	PANEL_ID,
	PANEL_WIDTH,
} from '@atlassian/jira-onboarding-checklist-next/src/common/constants.tsx';
import { QUICKSTART_UI_STATUS } from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import {
	useJswOnboarding,
	useQuickstartUiStatus,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { fireAnalyticsOnOpenRightHandSidebar } from '@atlassian/jira-onboarding-quickstart-core/src/utils/analytics/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { TriggersToOpenChecklist } from '../../common/types.tsx';

// We tend to combine flows of openChecklist from both minimised and dismissed

type OpenChecklistProps = {
	trigger: TriggersToOpenChecklist;
	openTask?: string;
};

export const useOpenChecklist = () => {
	const [
		{ openTask: currentOpenTask },
		{ nextSetChecklistIsOpen, nextSetChecklistIsDismissed, nextOpenTask, setIsOpenedViaButton },
	] = useJswOnboarding();
	const { openRightSidebar } = useRightSidebarController();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [quickstartStatus] = useQuickstartUiStatus();
	const currentRightSidebarState = useActiveRightSidebarState();

	const currentPanelId = currentRightSidebarState?.panelId;

	const openChecklist = async ({ trigger, openTask }: OpenChecklistProps) => {
		if (openTask && openTask !== currentOpenTask) {
			nextOpenTask(openTask);
		}

		// Reset the flag if the panel is opened from a different trigger (not the button)
		if (trigger !== 'JswOnboardingChecklistButton' && fg('jira_a11y_focus_quickstart_button')) {
			setIsOpenedViaButton(false);
		}

		openRightSidebar(PANEL_ID, PANEL_WIDTH, false, true);

		fireAnalyticsOnOpenRightHandSidebar(createAnalyticsEvent, {
			quickstartStatus,
			currentRightSidebarPanelId: currentPanelId || '',
			openRightSidebarToTop: true,
			trigger,
		});
		if (quickstartStatus === QUICKSTART_UI_STATUS.DISMISSED) {
			if (fg('jira_kuro-10551_fix_qs_flash')) {
				await nextSetChecklistIsDismissed(false);
			} else {
				nextSetChecklistIsDismissed(false);
			}
		} else {
			nextSetChecklistIsOpen(true);
		}
	};

	if (__SERVER__) {
		return noop;
	}

	return openChecklist;
};
